// src/components/ui/sections/BusinessSettingsList/BusinessSettingsListShadcn.jsx
import React from 'react';
import {
  Building2,
  Clock,
  Settings,
  Mail,
  Bell,
  CheckCircle,
  Circle,
  CreditCard,
  Plug2
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../card';
import { Button } from '../../button';
import { Badge } from '../../badge';

import { cn } from '../../../../utils/cn';

const BusinessSettingsListShadcn = ({
  selectedSection,
  onSelectSection,
  completionStatus = {},
  className = ''
}) => {
  // Settings sections configuration
  const settingsSections = [
    {
      id: 'company',
      title: 'Company Information',
      description: 'Basic business details and contact info',
      icon: Building2,
      completed: completionStatus.company || false
    },
    {
      id: 'hours',
      title: 'Business Hours',
      description: 'Operating hours for each day',
      icon: Clock,
      completed: completionStatus.hours || false
    },
    {
      id: 'operational',
      title: 'Operational Settings',
      description: 'Scheduling and business rules',
      icon: Settings,
      completed: completionStatus.operational || false
    },
    {
      id: 'email',
      title: 'Email Configuration',
      description: 'Email sending and domain setup',
      icon: Mail,
      completed: completionStatus.email || false
    },
    {
      id: 'notifications',
      title: 'Business Notifications',
      description: 'Alert preferences and recipients',
      icon: Bell,
      completed: completionStatus.notifications || false
    },
    {
      id: 'payments',
      title: 'Payment Processing',
      description: 'Stripe payment connection and settings',
      icon: CreditCard,
      completed: completionStatus.payments || false
    },
    {
      id: 'integrations',
      title: 'Integrations',
      description: 'Connect to Google Business Profile and other services',
      icon: Plug2,
      completed: completionStatus.integrations || false
    }
  ];

  const handleSectionSelect = (sectionId) => {
    if (onSelectSection) {
      onSelectSection(sectionId);
    }
  };

  const completedCount = Object.values(completionStatus).filter(Boolean).length;

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      {/* Header */}
      <CardHeader className="flex-shrink-0">
        <CardTitle>Settings</CardTitle>
        <CardDescription>Configure your business</CardDescription>
      </CardHeader>

      {/* Settings List */}
      <CardContent className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {settingsSections.map((section) => {
            const Icon = section.icon;
            const isSelected = selectedSection === section.id;

            return (
              <Button
                key={section.id}
                variant={isSelected ? "secondary" : "ghost"}
                onClick={() => handleSectionSelect(section.id)}
                className={cn(
                  'w-full justify-start h-auto p-3 transition-all duration-200',
                  isSelected
                    ? 'bg-primary/10 border-primary/30 shadow-sm'
                    : 'hover:bg-accent'
                )}
              >
                <div className="flex items-start gap-3 w-full">
                  {/* Icon */}
                  <div className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                    isSelected
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  )}>
                    <Icon className="w-4 h-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={cn(
                        'font-medium text-sm transition-colors',
                        isSelected ? 'text-foreground' : 'text-foreground'
                      )}>
                        {section.title}
                      </h3>

                      {/* Completion Status */}
                      {section.completed ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>

                    <p className={cn(
                      'text-xs transition-colors',
                      isSelected ? 'text-muted-foreground' : 'text-muted-foreground'
                    )}>
                      {section.description}
                    </p>
                  </div>
                </div>
              </Button>
            );
          })}
        </div>
      </CardContent>

      {/* Footer Stats */}
      <div className="p-4 border-t flex-shrink-0">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {completedCount} of {settingsSections.length} completed
          </span>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              Ready
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BusinessSettingsListShadcn;